// Main renderer module - orchestrates blueprint + life layers
// Pluggable into the main game loop

import { hexToPixel, pixelToHex, hexKey, hexCorners, getEffectiveSide } from '../math/hexMath.mjs';
import { worldToScreen, screenToWorld, getViewportWorldBounds } from '../math/camera.mjs';
import { drawBlueprintTile, drawPlacementIndicator } from './blueprint/blueprintDraw.mjs';
import { LifeChunkCache } from './life/LifeChunkCache.mjs';
import { analyseTwoTypeRun } from '../gdd-types/tileModel.mjs';
import { TILE_COLORS } from '../gdd-types/tileTypes.mjs';
import { isWaterTile, isWaterOverlay } from './assets/spriteIndex.mjs';
import { AnimalAnimator, isAnimal } from './animals/AnimalAnimator.mjs';
import { computeOverlayMotion, computeHoverSparkles } from './overlays/OverlayMotion.mjs';
import { CoastFx } from './fx/CoastFx.mjs';
import { BiomeTextureFx } from './fx/BiomeTextureFx.mjs';
import { OverlayDepthFx } from './fx/OverlayDepthFx.mjs';
import { BirdsFx } from './fx/BirdsFx.mjs';
import { renderMetaByCoord } from './adjacency/AdjacencyTransitions.mjs';

// Vertical offset (in sprite-space pixels, before scaling) to align atlas
// sprites with the hex grid. Adjust if tiles appear shifted vertically.
// Positive = move sprite down, negative = move sprite up.
export const SPRITE_OFFSET_Y = -2;

export class HexaworldsRenderer {
  /**
   * @param {object} opts
   * @param {HTMLCanvasElement} opts.lifeCanvas
   * @param {HTMLCanvasElement} opts.blueprintCanvas
   * @param {number} opts.hexSize
   * @param {number} opts.worldSeed
   * @param {import('./assets/SpriteAtlas.mjs').SpriteAtlas} [opts.spriteAtlas]
   * @param {object} [opts.spriteIndex]
   */
  constructor({ lifeCanvas, blueprintCanvas, hexSize, worldSeed, spriteAtlas, spriteIndex, cloudShadowRenderer }) {
    this.lifeCanvas = lifeCanvas;
    this.blueprintCanvas = blueprintCanvas;
    this.lifeCtx = lifeCanvas.getContext('2d');
    this.blueprintCtx = blueprintCanvas.getContext('2d');
    this.hexSize = hexSize;
    this.worldSeed = worldSeed;

    this.spriteAtlas = spriteAtlas || null;
    this.spriteIndex = spriteIndex || null;

    this.camera = { x: 0, y: 0, zoom: 1 };

    /** @type {Map<string, object>} */
    this.tiles = new Map();

    this.lifeCache = new LifeChunkCache(worldSeed, hexSize);

    // Hover state (set externally)
    this.hoveredHex = null;
    this.ghostTile = null; // tile to show as preview at hovered position
    this.ghostOverlayKey = null; // overlay key to show as preview on hovered placed tile
    this.validPlacements = new Set(); // set of "q,r" keys

    // Cloud shadows + animal animation
    this.cloudShadowRenderer = cloudShadowRenderer || null;
    this.animalAnimator = new AnimalAnimator();

    // Procedural FX
    this.coastFx        = new CoastFx();
    this.biomeTextureFx = new BiomeTextureFx();
    this.overlayDepthFx = new OverlayDepthFx();
    this.birdsFx        = new BirdsFx();

    // Debug flags (toggled by keyboard or querystring)
    const qs = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    this.debugFlags = {
      hideAdjacency: qs.has('noAdj'),
      hideInternal:  false,
      showEdges:     qs.has('showEdges'),
      showRuns:      qs.has('showRuns'),
    };
  }

  setCamera(cam) {
    if (cam.x !== undefined) this.camera.x = cam.x;
    if (cam.y !== undefined) this.camera.y = cam.y;
    if (cam.zoom !== undefined) this.camera.zoom = cam.zoom;
  }

  getCamera() {
    return { ...this.camera };
  }

  /** Provide world state as a map for fast access */
  setTiles(tiles) {
    this.tiles = tiles;
    this.lifeCache.setTiles(tiles);
  }

  /** Convenience: add/update a single tile */
  upsertTile(tile) {
    const key = hexKey(tile.coord.q, tile.coord.r);
    this.tiles.set(key, tile);
    // Invalidate life chunk at tile position
    const wp = hexToPixel(tile.coord.q, tile.coord.r, this.hexSize);
    this.lifeCache.invalidateAt(wp.x, wp.y);
  }

  /** Remove a tile */
  removeTile(coord) {
    const key = hexKey(coord.q, coord.r);
    this.tiles.delete(key);
    const wp = hexToPixel(coord.q, coord.r, this.hexSize);
    this.lifeCache.invalidateAt(wp.x, wp.y);
  }

  /** Called from main loop - schedules chunk rebuilds, anim timers */
  update(nowMs) {
    this.lifeCache.update(nowMs);
  }

  /**
   * Trigger a sparkle burst at the given hex position (called on overlay placement).
   * @param {number} q
   * @param {number} r
   */
  triggerOverlaySparkles(q, r) {
    const w = this.blueprintCanvas.width;
    const h = this.blueprintCanvas.height;
    const cam = this.camera;
    const wp = hexToPixel(q, r, this.hexSize);
    const sp = worldToScreen(cam, w, h, wp.x, wp.y);
    this.overlayDepthFx.spawnSparkles(sp.x, sp.y, performance.now());
  }

  /**
   * Assign baseSpriteKey and transitionSpriteKey to a tile using spriteIndex.
   * Call once at placement time for deterministic sprite selection.
   * @param {object} tile
   */
  assignSpriteKeys(tile) {
    if (!this.spriteIndex) return;
    const { tileFullKeysByType, tileTransitionKeys } = this.spriteIndex;

    // Get effective sides accounting for rotation
    const effectiveSides = [];
    for (let i = 0; i < 6; i++) {
      effectiveSides.push(tile.sides[((i - tile.rotation) % 6 + 6) % 6]);
    }

    const run = analyseTwoTypeRun(effectiveSides);

    // When ocean is involved, always use ocean as base tile
    if (run.b !== null && !run.invalid) {
      if (run.a !== 'ocean' && run.b === 'ocean') {
        // Ocean is minority — swap so ocean becomes base
        const oldA = run.a, oldACount = run.aCount, oldStart = run.startIndex;
        run.a = 'ocean';
        run.b = oldA;
        run.aCount = 6 - oldACount;
        run.startIndex = (oldStart + oldACount) % 6;
      }
    }

    // Pick a random base sprite for the majority type
    const fullKeys = tileFullKeysByType.get(run.a);
    if (fullKeys && fullKeys.length > 0) {
      const idx = Math.floor(Math.random() * fullKeys.length);
      tile.baseSpriteKey = fullKeys[idx];
    }

    // If two-type, pick transition sprite for the minority type
    if (run.b !== null && !run.invalid) {
      const minorCount = 6 - run.aCount; // X
      const startIndex = run.startIndex;
      // The minority run starts where the majority run ends
      // Find the start of the minority run
      const majorityEnd = (startIndex + run.aCount) % 6;
      // Convert from procedural side convention (side 0 = SE, corner 0 at 0°)
      // to atlas convention (side 0 = north). Offset is +2.
      const atlasY = (majorityEnd + 2) % 6;
      const lookupKey = `${run.b}:${minorCount}:${atlasY}`;
      tile.transitionSpriteKey = tileTransitionKeys.get(lookupKey) || null;
    } else {
      tile.transitionSpriteKey = null;
    }
  }

  /** Main render call - draws life then blueprint */
  render(nowMs) {
    const w = this.blueprintCanvas.width;
    const h = this.blueprintCanvas.height;
    const cam = this.camera;
    const sz = this.hexSize * cam.zoom;

    // Clear both canvases
    this.lifeCtx.clearRect(0, 0, w, h);
    this.blueprintCtx.clearRect(0, 0, w, h);

    // Background
    this.blueprintCtx.fillStyle = '#1a1a2e';
    this.blueprintCtx.fillRect(0, 0, w, h);

    // Get viewport bounds in world space
    const bounds = getViewportWorldBounds(cam, w, h);
    const buf = this.hexSize * 2;

    // --- Draw valid placement indicators ---
    for (const key of this.validPlacements) {
      const [q, r] = key.split(',').map(Number);
      const wp = hexToPixel(q, r, this.hexSize);
      // Viewport culling
      if (wp.x < bounds.minX - buf || wp.x > bounds.maxX + buf) continue;
      if (wp.y < bounds.minY - buf || wp.y > bounds.maxY + buf) continue;
      const sp = worldToScreen(cam, w, h, wp.x, wp.y);
      drawPlacementIndicator(this.blueprintCtx, sp.x, sp.y, sz);
    }

    const ctx = this.blueprintCtx;

    // --- Build sorted visible-tile list (top-to-bottom for isometric depth) ---
    const visibleTiles = [];
    for (const [key, tile] of this.tiles) {
      if (!tile.placed || !tile.coord) continue;
      const wp = hexToPixel(tile.coord.q, tile.coord.r, this.hexSize);
      if (wp.x < bounds.minX - buf || wp.x > bounds.maxX + buf) continue;
      if (wp.y < bounds.minY - buf || wp.y > bounds.maxY + buf) continue;
      const sp = worldToScreen(cam, w, h, wp.x, wp.y);
      visibleTiles.push({ tile, wp, sp });
    }
    visibleTiles.sort((a, b) => a.wp.y - b.wp.y || a.wp.x - b.wp.x);

    // --- PASS 1: Base tiles (clipped to hex boundary) ---
    for (const { tile, sp } of visibleTiles) {
      if (this.spriteAtlas && tile.baseSpriteKey) {
        ctx.save();
        this._clipHex(ctx, sp.x, sp.y, sz);
        this._drawAtlasTileBase(ctx, tile, sp.x, sp.y, sz);
        ctx.restore();
      } else {
        drawBlueprintTile(ctx, tile, sp.x, sp.y, sz, 1, false, true);
      }
    }

    // --- Debug overlay: edge types (E) and transition runs (T) ---
    if (this.debugFlags.showEdges || this.debugFlags.showRuns) {
      this._drawAdjacencyDebug(ctx, visibleTiles, sz);
    }

    // --- FX: Biome texture + Coastline foam/shimmer ---
    // Runs after base+transition sprites, before overlays.
    this.biomeTextureFx.render(ctx, visibleTiles, this.worldSeed, sz, nowMs, this.tiles);
    this.coastFx.render(ctx, visibleTiles, this.tiles, sz, nowMs);

    // --- SHADOW COMPOSITE: cloud shadows masked to tile + placement hexes ---
    if (this.cloudShadowRenderer) {
      // Build hex screen positions for masking
      const hexPositions = [];
      for (const { sp } of visibleTiles) {
        hexPositions.push(sp);
      }
      for (const key of this.validPlacements) {
        const [pq, pr] = key.split(',').map(Number);
        const pwp = hexToPixel(pq, pr, this.hexSize);
        if (pwp.x < bounds.minX - buf || pwp.x > bounds.maxX + buf) continue;
        if (pwp.y < bounds.minY - buf || pwp.y > bounds.maxY + buf) continue;
        hexPositions.push(worldToScreen(cam, w, h, pwp.x, pwp.y));
      }
      this.cloudShadowRenderer.composite(ctx, w, h, hexPositions, sz, hexCorners);
    }

    // --- PASS 2: Overlay layer (unclipped, can overflow hex boundary) ---
    const animSet = this.animalAnimator.prioritize(visibleTiles, w, h);
    for (let i = 0; i < visibleTiles.length; i++) {
      const { tile, sp } = visibleTiles[i];
      if (!tile.overlaySpriteKey) continue;
      // Draw contact shadow beneath the overlay sprite
      this.overlayDepthFx.drawShadow(ctx, sp.x, sp.y, sz);
      if (animSet.has(i)) {
        this.animalAnimator.draw(
          ctx, this.spriteAtlas, tile.overlaySpriteKey,
          sp.x, sp.y, sz,
          tile.coord.q, tile.coord.r,
          nowMs, SPRITE_OFFSET_Y
        );
      } else {
        this._drawAtlasTileOverlay(ctx, tile, sp.x, sp.y, sz, nowMs);
      }
    }

    // --- Sparkle burst particles (drawn above overlays) ---
    this.overlayDepthFx.renderSparkles(ctx, nowMs);

    // --- Birds flying from forest tiles ---
    this.birdsFx.render(ctx, visibleTiles, this.worldSeed, sz, nowMs, this.tiles);

    // --- Hover halo + sparkles on hovered tiles ---
    if (this.hoveredHex) {
      const hk = hexKey(this.hoveredHex.q, this.hoveredHex.r);
      const hovTile = this.tiles.get(hk);
      const hovWp = hexToPixel(this.hoveredHex.q, this.hoveredHex.r, this.hexSize);
      const hovSp = worldToScreen(cam, w, h, hovWp.x, hovWp.y);
      const hasOverlay = !!(hovTile && hovTile.overlaySpriteKey);

      // Hover halo ring (replaces the old faint ring)
      if (hovTile && hovTile.placed) {
        this.overlayDepthFx.drawHoverHalo(ctx, hovSp.x, hovSp.y, sz, hasOverlay);
      }

      // Existing hover sparkles on overlaid tiles
      if (hasOverlay) {
        const sparkles = computeHoverSparkles(
          this.hoveredHex.q, this.hoveredHex.r,
          this.worldSeed, nowMs, sz
        );
        ctx.save();
        for (const s of sparkles) {
          ctx.globalAlpha = s.alpha;
          ctx.fillStyle = '#fffbe6';
          ctx.beginPath();
          ctx.arc(hovSp.x + s.x, hovSp.y + s.y, s.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    // --- PASS 3: Hover layer (ghost tile / ghost overlay, always on top) ---
    if (this.hoveredHex && this.ghostTile) {
      const hk = hexKey(this.hoveredHex.q, this.hoveredHex.r);
      if (this.validPlacements.has(hk) && !this.tiles.has(hk)) {
        const wp = hexToPixel(this.hoveredHex.q, this.hoveredHex.r, this.hexSize);
        const sp = worldToScreen(cam, w, h, wp.x, wp.y);
        ctx.save();
        ctx.globalAlpha = 1.0;
        if (this.spriteAtlas && this.ghostTile.baseSpriteKey) {
          this._drawAtlasTile(ctx, this.ghostTile, sp.x, sp.y, sz);
        } else {
          drawBlueprintTile(ctx, this.ghostTile, sp.x, sp.y, sz, 1, false, true);
        }
        ctx.restore();
        // Draw highlight border on top
        const corners = hexCorners(sp.x, sp.y, sz);
        ctx.beginPath();
        for (let i = 0; i < corners.length; i++) {
          if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
          else ctx.lineTo(corners[i].x, corners[i].y);
        }
        ctx.closePath();
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
    }

    // --- Ghost overlay at hovered position ---
    if (this.hoveredHex && this.ghostOverlayKey) {
      const hk = hexKey(this.hoveredHex.q, this.hoveredHex.r);
      const tile = this.tiles.get(hk);
      if (tile && tile.placed && !tile.overlaySpriteKey) {
        const tileIsWater = tile.baseSpriteKey ? isWaterTile(tile.baseSpriteKey) : false;
        const overlayIsWater = isWaterOverlay(this.ghostOverlayKey);
        if (tileIsWater === overlayIsWater) {
          const wp = hexToPixel(this.hoveredHex.q, this.hoveredHex.r, this.hexSize);
          const sp = worldToScreen(cam, w, h, wp.x, wp.y);
          // Draw ghost overlay directly (unclipped) — base tile already rendered in passes 1-2
          ctx.save();
          ctx.globalAlpha = 1.0;
          const atlas = this.spriteAtlas;
          if (atlas && atlas.has(this.ghostOverlayKey)) {
            const scale = (2 * sz) / 30;
            const dx = sp.x - 15 * scale;
            const dy = sp.y - 15 * scale;
            ctx.translate(dx, dy);
            ctx.scale(scale, scale);
            atlas.draw(ctx, this.ghostOverlayKey, 0, SPRITE_OFFSET_Y);
          }
          ctx.restore();
          // Draw golden hex highlight border
          const corners = hexCorners(sp.x, sp.y, sz);
          ctx.beginPath();
          for (let i = 0; i < corners.length; i++) {
            if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
            else ctx.lineTo(corners[i].x, corners[i].y);
          }
          ctx.closePath();
          ctx.strokeStyle = '#ffd700';
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
      }
    }

    // --- Draw life overlay ---
    this.lifeCache.render(this.lifeCtx, cam, w, h, nowMs);
  }

  /**
   * Draw a tile using atlas sprites.
   * Sprites are 30×30 logical (hex artwork is 30×26 centered in the 30×30 frame).
   * We scale from 30 to the actual screen hex size.
   */
  _drawAtlasTile(ctx, tile, sx, sy, sz) {
    const atlas = this.spriteAtlas;
    // The sprite is 30×30, hex visual is 30×26 centered vertically.
    // The hex center in the sprite is at (15, 15).
    // sz is the screen hex radius. The hex width = 2*sz, height = sqrt(3)*sz.
    // In the sprite: hex width = 30, so scale = (2*sz) / 30.
    const scale = (2 * sz) / 30;
    // Top-left of the 30×30 sprite frame in screen coords:
    const dx = sx - 15 * scale;
    const dy = sy - 15 * scale;

    ctx.save();
    ctx.translate(dx, dy);
    ctx.scale(scale, scale);

    // 1. Base tile sprite (with vertical alignment offset)
    atlas.draw(ctx, tile.baseSpriteKey, 0, SPRITE_OFFSET_Y);

    // 2. Transition sprite overlay
    if (tile.transitionSpriteKey && atlas.has(tile.transitionSpriteKey)) {
      atlas.draw(ctx, tile.transitionSpriteKey, 0, SPRITE_OFFSET_Y);
    }

    // 3. Overlay sprite
    if (tile.overlaySpriteKey && atlas.has(tile.overlaySpriteKey)) {
      atlas.draw(ctx, tile.overlaySpriteKey, 0, SPRITE_OFFSET_Y);
    }

    ctx.restore();
  }

  /** Draw base sprite + internal transition + adjacency transitions (layers 1-3). */
  _drawAtlasTileBase(ctx, tile, sx, sy, sz) {
    const atlas = this.spriteAtlas;
    const scale = (2 * sz) / 30;
    const dx = sx - 15 * scale;
    const dy = sy - 15 * scale;

    ctx.save();
    ctx.translate(dx, dy);
    ctx.scale(scale, scale);

    // 1. Base tile sprite
    atlas.draw(ctx, tile.baseSpriteKey, 0, SPRITE_OFFSET_Y);

    // 2. Internal 2-type transition (existing behaviour)
    if (tile.transitionSpriteKey && atlas.has(tile.transitionSpriteKey) && !this.debugFlags.hideInternal) {
      atlas.draw(ctx, tile.transitionSpriteKey, 0, SPRITE_OFFSET_Y);
    }

    // 3. Adjacency transitions (new)
    if (!this.debugFlags.hideAdjacency && tile.coord) {
      const meta = renderMetaByCoord.get(hexKey(tile.coord.q, tile.coord.r));
      if (meta) {
        for (const t of meta.adjacencyTransitions) {
          if (t.spriteKey && atlas.has(t.spriteKey)) {
            atlas.draw(ctx, t.spriteKey, 0, SPRITE_OFFSET_Y);
          } else {
            this._drawProceduralAdjacencyWedge(ctx, t);
          }
        }
      }
    }

    ctx.restore();
  }

  /**
   * Draw a procedural wedge for one adjacency transition run.
   * Called inside the 30×30 sprite-space transform (after translate+scale).
   * @param {CanvasRenderingContext2D} ctx
   * @param {{ minorType: string, X: number, Y: number }} run
   */
  _drawProceduralAdjacencyWedge(ctx, run) {
    // Hex centre in 30×30 sprite space (accounting for SPRITE_OFFSET_Y)
    const cx = 15;
    const cy = 15 + SPRITE_OFFSET_Y; // 13
    const radius = 14;
    const reach = 0.50; // how far toward centre the wedge extends
    const color = TILE_COLORS[run.minorType] ?? '#888888';

    ctx.save();
    ctx.globalAlpha = 0.50;
    ctx.fillStyle = color;

    for (let e = 0; e < run.X; e++) {
      const edgeIdx = (run.Y + e) % 6;
      const angle1 = (Math.PI / 180) * (60 * edgeIdx);
      const angle2 = (Math.PI / 180) * (60 * ((edgeIdx + 1) % 6));

      const c1x = cx + radius * Math.cos(angle1);
      const c1y = cy + radius * Math.sin(angle1);
      const c2x = cx + radius * Math.cos(angle2);
      const c2y = cy + radius * Math.sin(angle2);

      // Inner (center-side) points of the trapezoid
      const i1x = c1x + (cx - c1x) * reach;
      const i1y = c1y + (cy - c1y) * reach;
      const i2x = c2x + (cx - c2x) * reach;
      const i2y = c2y + (cy - c2y) * reach;

      ctx.beginPath();
      ctx.moveTo(i1x, i1y);
      ctx.lineTo(c1x, c1y);
      ctx.lineTo(c2x, c2y);
      ctx.lineTo(i2x, i2y);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  /** Draw overlay sprite only (layer 3); no-op if tile has no overlay. */
  _drawAtlasTileOverlay(ctx, tile, sx, sy, sz, nowMs) {
    const atlas = this.spriteAtlas;
    if (!tile.overlaySpriteKey || !atlas || !atlas.has(tile.overlaySpriteKey)) return;

    const motion = computeOverlayMotion(
      tile.overlaySpriteKey,
      tile.coord.q, tile.coord.r,
      this.worldSeed, nowMs
    );

    const scale = (2 * sz) / 30;

    ctx.save();
    // Translate to tile screen centre, apply motion, then offset to sprite top-left
    ctx.translate(sx + motion.tx, sy + motion.ty);
    if (motion.rotation !== 0) ctx.rotate(motion.rotation);
    if (motion.scale !== 1) ctx.scale(motion.scale, motion.scale);
    ctx.translate(-15 * scale, -15 * scale);
    ctx.scale(scale, scale);
    atlas.draw(ctx, tile.overlaySpriteKey, 0, SPRITE_OFFSET_Y);
    ctx.restore();
  }

  /** Set a hex-shaped clipping region centered at (cx, cy) with radius sz. */
  _clipHex(ctx, cx, cy, sz) {
    const corners = hexCorners(cx, cy, sz);
    ctx.beginPath();
    for (let i = 0; i < corners.length; i++) {
      if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
      else ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
    ctx.clip();
  }

  /**
   * Draw adjacency debug overlays: edge type labels (showEdges) and run labels (showRuns).
   * Drawn in screen space after PASS 1.
   */
  _drawAdjacencyDebug(ctx, visibleTiles, sz) {
    const fontSize = Math.max(6, Math.round(sz * 0.22));
    ctx.save();
    ctx.font = `${fontSize}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (const { tile, sp } of visibleTiles) {
      if (!tile.coord) continue;

      if (this.debugFlags.showEdges) {
        const corners = hexCorners(sp.x, sp.y, sz);
        const rot = tile.rotation ?? 0;
        for (let i = 0; i < 6; i++) {
          const c1 = corners[i];
          const c2 = corners[(i + 1) % 6];
          // Midpoint of the edge, nudged inward slightly
          const mx = (c1.x + c2.x) / 2;
          const my = (c1.y + c2.y) / 2;
          const px = mx + (sp.x - mx) * 0.18;
          const py = my + (sp.y - my) * 0.18;
          const edgeType = getEffectiveSide(tile.sides, rot, i);
          const label = `${i}:${edgeType.slice(0, 2)}`;
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#000';
          ctx.strokeText(label, px, py);
          ctx.fillStyle = '#ffff00';
          ctx.fillText(label, px, py);
        }
      }

      if (this.debugFlags.showRuns) {
        const meta = renderMetaByCoord.get(hexKey(tile.coord.q, tile.coord.r));
        if (meta && meta.adjacencyTransitions.length > 0) {
          const lines = meta.adjacencyTransitions.map(
            t => `${t.minorType.slice(0, 3)} ${t.X}-${t.Y}${t.spriteKey ? '' : ' ?'}`
          );
          const lineH = Math.max(8, Math.round(sz * 0.26));
          const startY = sp.y - lineH * (lines.length - 1) / 2;
          ctx.lineWidth = 2;
          lines.forEach((line, j) => {
            ctx.strokeStyle = '#000';
            ctx.strokeText(line, sp.x, startY + j * lineH);
            ctx.fillStyle = '#00ff88';
            ctx.fillText(line, sp.x, startY + j * lineH);
          });
        }
      }
    }

    ctx.restore();
  }

  /** Add a hex subpath (no beginPath/clip) for batched clipping. */
  _addHexSubpath(ctx, cx, cy, sz) {
    const corners = hexCorners(cx, cy, sz);
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
  }

  /**
   * Convert screen coordinates to axial hex coordinates.
   * @param {number} screenX
   * @param {number} screenY
   * @returns {{ q: number, r: number } | null}
   */
  screenToAxial(screenX, screenY) {
    const w = this.blueprintCanvas.width;
    const h = this.blueprintCanvas.height;
    const world = screenToWorld(this.camera, w, h, screenX, screenY);
    return pixelToHex(world.x, world.y, this.hexSize);
  }
}
